import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message
      });

      toast.success('Message sent successfully! We will get back to you soon.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Touch</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Ready to optimize your IT infrastructure? Contact us today for a free consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800 backdrop-blur-sm hover:border-cyan-500/30 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Email Us</h3>
                  <a href="mailto:info@frostbyteitmn.com" className="text-slate-400 hover:text-cyan-400 transition-colors block">
                    info@frostbyteitmn.com
                  </a>
                  <a href="mailto:support@frostbyteitmn.com" className="text-slate-400 hover:text-cyan-400 transition-colors block">
                    support@frostbyteitmn.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800 backdrop-blur-sm hover:border-cyan-500/30 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Call Us</h3>
                  <a href="tel:+12184056066" className="hover:text-cyan-400 transition-colors block !text-lg text-slate-400">218-234-6179

                  </a>
                  <p className="!text-sm text-slate-400">Mon-Fri 8AM-6PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-slate-900/50 p-8 rounded-2xl border border-slate-800 backdrop-blur-sm space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-white font-medium">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 transition-colors"
                    placeholder="Your full name" />

                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-white font-medium">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 transition-colors"
                    placeholder="your@email.com" />

                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="phone" className="text-white font-medium">
                    Phone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 transition-colors"
                    placeholder="+1 (555) 123-4567" />

                </div>
                <div className="space-y-2">
                  <label htmlFor="subject" className="text-white font-medium">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    data-testid="contact-subject-input"
                    value={formData.subject}
                    onChange={handleChange}
                    className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 transition-colors"
                    placeholder="How can we help?" />

                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="text-white font-medium">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 transition-colors resize-none"
                  placeholder="Tell us about your IT needs..." />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

export default Contact;